import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, comicArticles, getCategoryBySlug } from '@/mocks/comics';
import { getWebPageSchema, getBreadcrumbSchema, getComicArticleSchema } from '@/mocks/schemas';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  useEffect(() => {
    if (comic) {
      document.title = comic.seoTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', comic.seoDescription);
      }
    }
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">抱歉，您要查找的漫画不存在或已被移除。</p>
            <Link to="/" className="inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comicArticles
    .filter(c => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const categoryInfo = getCategoryBySlug(comic.category);

  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(0)}万`;
    }
    return views.toString();
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <JsonLd schema={getWebPageSchema(
          comic.seoTitle,
          comic.seoDescription,
          `https://www.manhuakr.com/article/${comic.id}`
        )} />
        <JsonLd schema={getBreadcrumbSchema([
          { name: '首页', item: 'https://www.manhuakr.com' },
          { name: categoryInfo?.name || comic.category, item: `https://www.manhuakr.com/category/${comic.category}` },
          { name: comic.title },
        ])} />
        <JsonLd schema={getComicArticleSchema(
          comic.seoTitle,
          comic.seoDescription,
          `https://www.manhuakr.com/article/${comic.id}`,
          comic.coverImage,
          comic.author,
          comic.updatedDate,
        )} />

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryInfo?.name || comic.category, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-6 md:gap-8 mb-10">
            <div className="flex-shrink-0 w-full sm:w-64 mx-auto lg:mx-0">
              <div className="aspect-[2/3] rounded-xl overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫在线阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {comic.title}
                </h1>
                <div className="flex items-center gap-1 bg-accent-100 text-accent-700 px-3 py-1 rounded-full">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                  <span className="text-sm font-bold">{comic.rating}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-foreground-500">
                <span className="whitespace-nowrap">作者：<strong className="text-foreground-800 font-medium">{comic.author}</strong></span>
                <span className="text-foreground-300">|</span>
                <span className="whitespace-nowrap">已更新 <strong className="text-foreground-800 font-medium">{comic.chapters}话</strong></span>
                <span className="text-foreground-300">|</span>
                <span className="whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-eye-line text-xs"></i>
                    </div>
                    {formatViews(comic.views)} 次阅读
                  </span>
                </span>
                <span className="text-foreground-300">|</span>
                <span className="whitespace-nowrap">更新于 {comic.updatedDate}</span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link
                  to={`/category/${comic.category}`}
                  className="flex-1 sm:flex-none px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-bold rounded-full text-center transition-all duration-300 whitespace-nowrap text-sm"
                >
                  开始阅读
                </Link>
                <button className="flex-1 sm:flex-none px-6 py-3 bg-secondary-100 hover:bg-secondary-200 text-foreground-700 font-medium rounded-full transition-all duration-300 whitespace-nowrap text-sm">
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bookmark-line"></i>
                    </div>
                    加入收藏
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-secondary-200/70 pt-8">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">漫画简介</h2>
            <p className="text-foreground-700 leading-relaxed text-sm">{comic.description}</p>
          </div>

          <div className="border-t border-secondary-200/70 pt-8 mt-8">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">精彩预览</h2>
            <div
              className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: comic.content }}
            />
          </div>
        </article>

        {relatedComics.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="border-t border-secondary-200/70 pt-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors whitespace-nowrap"
                >
                  查看更多
                  <span className="ml-1">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((related) => (
                  <ComicCard key={related.id} comic={related} variant="featured" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}